import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import 'react-datepicker/dist/react-datepicker.css';
import './NSDatePicker.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';

interface NSDatePickerProps {
    value: Date | null;
    onChange: (date: Date | null) => void;
    dateFormat?: string;
    showMonthYearPicker?: boolean;
    placeholderText?: string;
    disabled?: boolean;
    name?: string;
    className?: string;
    minDate?: Date;
    maxDate?: Date;
    onBlur?: () => void;
    calendarWidth?: string;
    label?: string;
    required?: boolean;
    showErrorMsg?: boolean;
    errorMsg?: string | null;
    // `input` (default) renders a form-control input with a calendar icon. `inline` renders just
    // the formatted date (or placeholder) as a clickable, italic-when-empty text — used inside
    variant?: 'input' | 'inline';
}

const NSDatePicker = ({
    value,
    onChange,
    dateFormat = 'MMMM yyyy',
    showMonthYearPicker = false,
    placeholderText = 'Select date',
    disabled = false,
    name,
    className = '',
    minDate,
    maxDate,
    onBlur,
    calendarWidth,
    label,
    required = false,
    showErrorMsg = false,
    errorMsg,
    variant = 'input',
}: NSDatePickerProps) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);
    const isSelectingDate = useRef(false);

    const formatDateForDisplay = (date: Date | null): string => {
        if (!date) return '';
        return format(date, dateFormat);
    };

    const handleDateChange = (date: Date | null) => {
        isSelectingDate.current = true;
        onChange(date);
        setIsPickerOpen(false);
        inputRef.current?.focus();
        setTimeout(() => {
            isSelectingDate.current = false;
        }, 0);
    };

    const handleInputClick = () => {
        if (!disabled) setIsPickerOpen(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === '') onChange(null);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
            onChange(null);
            e.preventDefault();
        }
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Don't trigger onBlur if we're clicking on the calendar
        if (isSelectingDate.current) return;

        // Check if the new focus target is inside the calendar
        if (calendarRef.current && calendarRef.current.contains(e.relatedTarget as Node)) return;

        if (onBlur) onBlur();
    };

    const closePicker = () => setIsPickerOpen(false);

    return (
        <>
            {label && (
                <label className="mb-1 font-weight-normal text-dark">
                    {label}
                    {required && <span className="text-danger"> *</span>}
                </label>
            )}
            <div className={classNames('NSDatePicker', { 'NSDatePicker--inline': variant === 'inline' })}>
                <RenderIf isTrue={variant === 'inline'}>
                    <div
                        role="button"
                        tabIndex={0}
                        className={classNames('text-primary cursor--pointer', { 'font-italic': !value }, className)}
                        onClick={handleInputClick}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleInputClick();
                            }
                        }}
                    >
                        {value ? formatDateForDisplay(value) : placeholderText}
                    </div>
                </RenderIf>
                <RenderIf isTrue={variant === 'input'}>
                    <>
                        <input
                            ref={inputRef}
                            type="text"
                            name={name}
                            value={formatDateForDisplay(value)}
                            onChange={handleInputChange}
                            onClick={handleInputClick}
                            onKeyDown={handleInputKeyDown}
                            onBlur={handleInputBlur}
                            className={`form-control ${className}`}
                            placeholder={placeholderText}
                            disabled={disabled}
                            readOnly
                        />
                        <button
                            type="button"
                            className="NSDatePicker__calendar-icon"
                            onClick={handleInputClick}
                            disabled={disabled}
                            tabIndex={-1}
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                        </button>
                    </>
                </RenderIf>
                <RenderIf isTrue={isPickerOpen}>
                    <div
                        className="NSDatePicker__calendar"
                        ref={calendarRef}
                        style={calendarWidth ? { width: calendarWidth } : undefined}
                    >
                        <DatePicker
                            selected={value}
                            onChange={handleDateChange}
                            dateFormat={dateFormat}
                            showMonthYearPicker={showMonthYearPicker}
                            inline
                            onClickOutside={closePicker}
                            minDate={minDate}
                            maxDate={maxDate}
                        />
                    </div>
                </RenderIf>
            </div>
            {showErrorMsg && errorMsg && (
                <p className="text-danger small mt-1 mb-0">{errorMsg}</p>
            )}
        </>
    );
};

export default NSDatePicker;
